import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to make API calls with better error handling
const apiCall = async (endpoint, options = {}) => {
  try {
    // Get token from localStorage for authentication
    const token = localStorage.getItem('token');
    const headers = {
      ...options.headers,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log(`[API] Calling: ${API_URL}${endpoint}`);
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
    return response;
  } catch (err) {
    console.error('API call failed:', endpoint, err);
    // Provide more specific error message
    if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
      throw new Error('Unable to connect to server. Please try again in a moment.');
    }
    throw new Error('Network error. Please check your connection.');
  }
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      const response = await apiCall('/api/auth/me');
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await apiCall('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
        } catch {
          // If JSON parsing fails, use status-based message
          if (response.status === 401) {
            errorMessage = 'Email or password is incorrect';
          } else {
            errorMessage = `Server error (${response.status})`;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Check if 2FA is required (admin login)
      if (data.requires_2fa) {
        return data;  // Return the 2FA challenge to the login page
      }
      
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return data.user;
    } catch (err) {
      const message = err.message || 'Login failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email, password, name, ageConfirmed, referralCode = null) => {
    setError(null);
    try {
      const requestBody = { 
        email, 
        password, 
        name, 
        age_confirmed: ageConfirmed 
      };
      
      // Add referral code if provided
      if (referralCode && referralCode.trim()) {
        requestBody.referral_code = referralCode.trim().toUpperCase();
      }
      
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      // Read response body once
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      return data;
    } catch (err) {
      // Handle network errors that don't have a proper message
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Session exchange failed');
      }

      const userData = await response.json();
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const toggleFavoriteTeam = async (teamId) => {
    try {
      const response = await fetch(`${API_URL}/api/user/favorites/team/${teamId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, favorite_teams: data.favorites }));
        return data;
      }
    } catch (err) {
      console.error('Toggle favorite team error:', err);
    }
  };

  const toggleFavoriteLeague = async (leagueId) => {
    try {
      const response = await fetch(`${API_URL}/api/user/favorites/league/${leagueId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, favorite_leagues: data.favorites }));
        return data;
      }
    } catch (err) {
      console.error('Toggle favorite league error:', err);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    exchangeSession,
    checkAuth,
    toggleFavoriteTeam,
    toggleFavoriteLeague,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isSubscriber: user?.is_subscriber || false,
    favoriteTeams: user?.favorite_teams || [],
    favoriteLeagues: user?.favorite_leagues || [],
    referralCode: user?.referral_code || null,
    credits: user?.credits || 0,
    referredBy: user?.referred_by || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
